import React, { useCallback, useEffect, useState } from 'react';
import { FiCheck, FiCopy, FiFileText, FiMessageCircle, FiUpload } from 'react-icons/fi';
import { clientJourneysAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './ClientJourneyPanel.css';

// Journey, consent, client link, pre-visa document checklist and (medical) payment ledger for a
// job seeker or medical client: the student-vertical features, shared by both pages.

const PURPOSE_LABEL = {
  SHARE_WITH_REFERRER: 'Share progress with the referring agent',
  HEALTH_DATA: 'Health-data consent (needed before any medical file)',
};

const errText = (e, fallback) => {
  const d = e?.response?.data || {};
  return d.error || d.detail || Object.values(d).flat()[0] || fallback;
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

async function saveBlob(promise, name) {
  const res = await promise;
  const type = res.headers?.['content-type'] || 'application/octet-stream';
  const ext = type.includes('pdf') ? 'pdf' : type.includes('png') ? 'png' : type.includes('jpeg') ? 'jpg' : 'bin';
  const url = URL.createObjectURL(new Blob([res.data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name}.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function Timeline({ data }) {
  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title"><span>Journey</span><span className="cj-muted">{data.status_display}</span></div>
      <ol className="cj-timeline">
        {data.timeline.map((s) => (
          <li key={s.step} className={s.done ? 'cj-done' : ''}>
            <span className="cj-dot">{s.done ? <FiCheck size={10} /> : null}</span>
            <span>{s.label}</span>
            {s.done && s.reached_at && <span className="cj-muted"> · {fmt(s.reached_at)}</span>}
          </li>
        ))}
      </ol>
      <div className="cj-waiting"><strong>Waiting on:</strong> {data.waiting_on}</div>
    </div>
  );
}

function Consents({ kind, id, data, onChanged }) {
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('');
  const [err, setErr] = useState('');
  const active = (p) => data.consents.find((c) => c.purpose === p && !c.withdrawn_at);
  const record = async (p, withdraw = false) => {
    setErr('');
    try {
      await clientJourneysAPI.consent(kind, id, withdraw ? { purpose: p, withdraw: true } : { purpose: p, respondent_name: name });
      setPurpose(''); setName(''); onChanged();
    } catch (e) { setErr(errText(e, 'Could not save the consent.')); }
  };
  if (!data.consents_required.length) return null;
  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title"><span>Consent</span></div>
      {data.consents_required.map((p) => {
        const c = active(p);
        return (
          <div key={p} className="cv-doc-row">
            <span className={`cj-pill ${c ? 'cj-ok' : 'cj-warn'}`}>{c ? 'Given' : 'Not yet'}</span>
            <span style={{ flex: 1 }}>{PURPOSE_LABEL[p]}
              {c && <span className="cj-muted"> · {c.respondent_name}, {fmt(c.given_at)} ({c.source_display})</span>}
            </span>
            {c
              ? <button type="button" className="cv-inline-btn" onClick={() => window.confirm('Withdraw this consent?') && record(p, true)}>Withdraw</button>
              : <button type="button" className="cv-inline-btn" onClick={() => setPurpose(p)}>Record signed form</button>}
          </div>
        );
      })}
      {purpose && (
        <div className="cj-form">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name of the person who signed" />
          <button type="button" className="cv-inline-btn" onClick={() => record(purpose)}>Save</button>
          <button type="button" className="cv-inline-btn" onClick={() => setPurpose('')}>Cancel</button>
        </div>
      )}
      <div className="cj-muted" style={{ marginTop: 6 }}>Clients usually agree on their own link. Record a signed paper form here.</div>
      {err && <div className="error-message">{err}</div>}
    </div>
  );
}

function LinkBox({ kind, id, data, onChanged }) {
  const [link, setLink] = useState(data.link);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState('');
  const run = async (fn) => {
    setErr('');
    try { const r = await fn(); setLink(r.data); onChanged(); } catch (e) { setErr(errText(e, 'Could not get the link.')); }
  };
  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title"><span>Client link</span><span className="cj-muted">no login: consent, then uploads</span></div>
      {link ? (
        <>
          <div className="cj-url">{link.url}</div>
          <div className="cj-actions">
            <button type="button" className="cv-inline-btn" onClick={() => navigator.clipboard.writeText(link.url).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}>
              {copied ? <FiCheck size={12} /> : <FiCopy size={12} />} {copied ? 'Copied' : 'Copy'}
            </button>
            {link.whatsapp_url && <a className="cv-inline-btn" href={link.whatsapp_url} target="_blank" rel="noreferrer"><FiMessageCircle size={12} /> WhatsApp</a>}
            <button type="button" className="cv-inline-btn" onClick={() => run(() => clientJourneysAPI.linkOp(kind, id, 'regenerate'))}>New link</button>
            <button type="button" className="cv-inline-btn" onClick={() => run(() => clientJourneysAPI.linkOp(kind, id, 'revoke'))}>Turn off</button>
          </div>
          {!link.is_valid && <div className="error-message">This link is turned off.</div>}
        </>
      ) : (
        <button type="button" className="cv-inline-btn" onClick={() => run(() => clientJourneysAPI.link(kind, id))}>Create client link</button>
      )}
      {err && <div className="error-message">{err}</div>}
    </div>
  );
}

function ItemRow({ kind, id, item, uploadsOpen, onChanged }) {
  const [mode, setMode] = useState(null);
  const [note, setNote] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true); setErr('');
    try { await fn(); setMode(null); setNote(''); onChanged(); } catch (e) { setErr(errText(e, 'That did not work.')); if (e?.response?.status === 409) onChanged(); }
    finally { setBusy(false); }
  };
  const latest = item.files[item.files.length - 1];
  return (
    <div className="cj-item">
      <div className="cv-doc-row">
        <span className={`cj-pill cj-${item.status.toLowerCase()}`}>{item.status_display}</span>
        <span style={{ flex: 1 }}>
          <strong>{item.label}</strong>{item.is_optional && <span className="cj-muted"> (optional)</span>}
          {item.rejection_note && <span className="cj-rej"> · Rejected: {item.rejection_note}</span>}
        </span>
        {item.files.map((f) => (
          <button key={f.which} type="button" className="cv-inline-btn" title={f.name}
            onClick={() => saveBlob(clientJourneysAPI.file(kind, id, item.key, f.which), `${item.key}_${f.which}`).catch(() => setErr('Could not download.'))}>
            <FiFileText size={12} /> {f.which}
          </button>
        ))}
        {item.status === 'UPLOADED_AWAITING_CHECK' && latest && (
          <>
            <button type="button" className="cv-inline-btn" disabled={busy} onClick={() => run(() => clientJourneysAPI.itemOp(kind, id, item.key, 'accept', { upload_version: item.upload_version }))}>Accept</button>
            <button type="button" className="cv-inline-btn" onClick={() => setMode('reject')}>Reject</button>
          </>
        )}
        {uploadsOpen && item.status !== 'RECEIVED' && item.status !== 'NOT_NEEDED' && (
          <label className="cv-inline-btn" style={{ cursor: 'pointer' }}>
            <FiUpload size={12} /> Attach
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
              onChange={(e) => e.target.files[0] && run(() => clientJourneysAPI.upload(kind, id, item.key, e.target.files[0]))} />
          </label>
        )}
        {item.status === 'NOT_NEEDED'
          ? <button type="button" className="cv-inline-btn" disabled={busy} onClick={() => run(() => clientJourneysAPI.itemOp(kind, id, item.key, 'reopen'))}>Needed</button>
          : item.status !== 'RECEIVED' && <button type="button" className="cv-inline-btn" disabled={busy} onClick={() => run(() => clientJourneysAPI.itemOp(kind, id, item.key, 'not-needed'))}>Not needed</button>}
      </div>
      {item.guidance && <div className="cj-muted cj-guidance">{item.guidance}</div>}
      {mode === 'reject' && (
        <div className="cj-form">
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why, so the client can fix it" />
          <button type="button" className="cv-inline-btn" disabled={busy} onClick={() => run(() => clientJourneysAPI.itemOp(kind, id, item.key, 'reject', { note, upload_version: item.upload_version }))}>Reject</button>
          <button type="button" className="cv-inline-btn" onClick={() => setMode(null)}>Cancel</button>
        </div>
      )}
      {err && <div className="error-message">{err}</div>}
    </div>
  );
}

function Checklist({ kind, id, data, onChanged }) {
  const p = data.progress;
  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title">
        <span>{kind === 'medical' ? 'Medical documents' : 'CV & documents'}</span>
        <span className="cj-muted">{p.required_received}/{p.required_total} required received{p.awaiting_check ? ` · ${p.awaiting_check} to check` : ''}</span>
      </div>
      {!data.uploads_open && <div className="cj-warn-box">No medical file can be collected until the client gives health-data consent.</div>}
      {data.items.map((i) => <ItemRow key={i.key} kind={kind} id={id} item={i} uploadsOpen={data.uploads_open} onChanged={onChanged} />)}
      {kind === 'job' && <div className="cj-muted" style={{ marginTop: 6 }}>From the destination's required documents. Accepting one also ticks it in Readiness. Visa documents live in Visa Cases.</div>}
    </div>
  );
}

const DESCRIPTIONS = [
  ['VEC_SERVICE_FEE', 'VisionExploit service fee'], ['HOSPITAL_DEPOSIT', 'Hospital deposit'],
  ['TREATMENT_BALANCE', 'Treatment balance'], ['ACCOMMODATION', 'Accommodation'], ['FLIGHTS', 'Flights'],
  ['VISA_FEE', 'Visa fee'], ['OTHER', 'Other'],
];

// Founder rule, 19 Oct 2026: only the CFO records a payment. The CFO's entries are Received and
// they accept/reject the rest; anyone else logs one that waits for the CFO (the server enforces it).
function Payments({ id, data, onChanged }) {
  const { canRecordPayments } = useAuth();
  const isRecorder = canRecordPayments();
  const [form, setForm] = useState(null);
  const [err, setErr] = useState('');
  const save = async () => {
    setErr('');
    try { await clientJourneysAPI.addPayment(id, form); setForm(null); onChanged(); } catch (e) { setErr(errText(e, 'Could not save the payment.')); }
  };
  const act = async (pid, op) => {
    setErr('');
    const body = op === 'reject' ? { note: window.prompt('Why is this payment rejected?') || '' } : {};
    if (op === 'reject' && !body.note) return;
    try { await clientJourneysAPI.paymentOp(id, pid, op, body); onChanged(); } catch (e) { setErr(errText(e, 'That did not work.')); }
  };
  const totals = Object.entries(data.payments_received || {}).map(([c, a]) => `${c} ${a}`).join(' · ');
  return (
    <div className="cv-subsection">
      <div className="cv-subsection-title">
        <span>Payments</span>
        <button type="button" className="cv-inline-btn" onClick={() => setForm(form ? null : { description: 'HOSPITAL_DEPOSIT', currency: 'USD' })}>{form ? 'Cancel' : (isRecorder ? 'Record payment' : 'Log payment for CFO / Chairman')}</button>
      </div>
      {totals && <div className="cj-muted" style={{ marginBottom: 6 }}>Received: {totals}</div>}
      {form && (
        <div className="cj-form cj-form-wrap">
          <select value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}>
            {DESCRIPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {form.description === 'OTHER' && <input placeholder="What for" value={form.other_description || ''} onChange={(e) => setForm({ ...form, other_description: e.target.value })} />}
          <input type="number" min="0" step="0.01" placeholder="Amount" value={form.amount || ''} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <input style={{ width: 70 }} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} />
          <input type="date" value={form.paid_on || ''} onChange={(e) => setForm({ ...form, paid_on: e.target.value })} />
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setForm({ ...form, receipt: e.target.files[0] })} />
          <button type="button" className="cv-inline-btn" onClick={save}>{isRecorder ? 'Record' : 'Send for check'}</button>
          {!isRecorder && <div className="cj-muted" style={{ width: '100%' }}>Only the CFO or Chairman records payments. This waits for their check before it counts as received.</div>}
        </div>
      )}
      {data.payments.length === 0 && !form && <div className="cv-subsection-empty">No payments yet.</div>}
      {data.payments.map((p) => (
        <div key={p.id} className="cv-doc-row">
          <span className={`cj-pill cj-${p.status.toLowerCase()}`}>{p.status_display}</span>
          <span style={{ flex: 1 }}>
            {p.description === 'OTHER' ? p.other_description : p.description_display} · <strong>{p.currency} {p.amount}</strong>
            {p.paid_on && <span className="cj-muted"> · paid {fmt(p.paid_on)}</span>}
            {p.added_via_public_link && <span className="cj-muted"> · from client</span>}
            {p.rejection_note && <span className="cj-rej"> · {p.rejection_note}</span>}
          </span>
          {p.has_receipt && <button type="button" className="cv-inline-btn" onClick={() => saveBlob(clientJourneysAPI.receipt(id, p.id), `receipt_${p.id}`).catch(() => setErr('Could not download.'))}><FiFileText size={12} /> receipt</button>}
          {p.status === 'UPLOADED_AWAITING_CHECK' && (isRecorder ? (
            <>
              <button type="button" className="cv-inline-btn" onClick={() => act(p.id, 'accept')}>Accept</button>
              <button type="button" className="cv-inline-btn" onClick={() => act(p.id, 'reject')}>Reject</button>
            </>
          ) : <span className="cj-muted">waiting for the CFO or Chairman</span>)}
        </div>
      ))}
      {err && <div className="error-message">{err}</div>}
    </div>
  );
}

// The medical payment ledger on its own, for the CFO's Payments desk: the CFO reaches the ledger
// endpoint but not the rest of the client's journey.
export function MedicalPaymentsPanel({ clientId, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    clientJourneysAPI.payments(clientId)
      .then((r) => { setData({ payments: r.data.payments, payments_received: r.data.received }); setErr(''); })
      .catch((e) => setErr(errText(e, 'Could not load the payments.')));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="cv-subsection"><div className="cv-subsection-empty">{err || 'Loading payments…'}</div></div>;
  return <div className="cj-panel"><Payments id={clientId} data={data} onChanged={() => { load(); onChanged?.(); }} /></div>;
}

export default function ClientJourneyPanel({ kind, clientId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => {
    clientJourneysAPI.get(kind, clientId).then((r) => { setData(r.data); setErr(''); })
      .catch((e) => setErr(errText(e, 'Could not load the journey.')));
  }, [kind, clientId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="cv-subsection"><div className="cv-subsection-empty">{err || 'Loading journey…'}</div></div>;
  return (
    <div className="cj-panel">
      <Timeline data={data} />
      <Consents kind={kind} id={clientId} data={data} onChanged={load} />
      <LinkBox kind={kind} id={clientId} data={data} onChanged={load} />
      <Checklist kind={kind} id={clientId} data={data} onChanged={load} />
      {kind === 'medical' && <Payments id={clientId} data={data} onChanged={load} />}
    </div>
  );
}
